<script>
import {defineComponent} from 'vue'

export default defineComponent({
  name: "TicketSidebar",
  props: {
    title: {
      type: String,
      default: 'Sidebar'
    },
    item: {
      type: Object,
      default: null
    },
    fields: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'form', // 'form' | 'custom'
      validator: (value) => ['form', 'custom'].includes(value)
    }
  },
  data() {
    return {
      formData: {}
    }
  },
  methods: {
    handleSubmit() {
      this.$emit('save', this.formData)
    },
    handleClose() {
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    handleHidden() {
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      this.formData = {}
    },
    initFormData() {
      if (this.item) {
        this.formData = {...this.item}
      } else {
        this.formData = this.fields.reduce((acc, field) => {
          acc[field.key] = field.default !== undefined ? field.default : null
          return acc
        }, {})
      }
    }
  },
  watch: {
    item: {
      immediate: true,
      handler() {
        this.initFormData()
      }
    }
  },
})
</script>

<template>
  <div>
    <b-sidebar backdrop id="sidebar-right" :title="$props.title" no-header
               :width="this.$props.mode === 'custom' ? '530px' : '320px'" right shadow @hidden="handleHidden">
      <div class="px-3 py-2 d-flex justify-content-between align-items-start">
        <p class="font-weight-bold">{{ title }}</p>
        <div style="font-size: 20px; cursor: pointer" @click.stop="handleClose">
          &#10005;
        </div>
      </div>
      <div class="sidebar-body px-3 py-2">
        <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100">
          <b-spinner></b-spinner>
        </div>
        <div v-else-if="mode === 'custom'" class="sidebar-custom-wrapper">
          <slot name="custom-content" :item="item" :close="handleClose"></slot>
        </div>
        <b-form @submit.prevent="handleSubmit" v-else>
          <div v-for="field in fields" :key="field.key" class="mb-3">
            <!-- Text Input -->
            <b-form-group
                v-if="field.type === 'text' || !field.type"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
              />
            </b-form-group>

            <!-- Textarea -->
            <b-form-group
                v-else-if="field.type === 'textarea'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-textarea
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
                  rows="3"
              />
            </b-form-group>

            <!-- Select -->
            <b-form-group
                v-else-if="field.type === 'select'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-select
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :options="field.options"
                  :required="field.required"
              >
                <template #first v-if="field.placeholder">
                  <b-form-select-option :value="null" disabled>
                    {{ field.placeholder }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <!-- Checkbox -->
            <b-form-group v-else-if="field.type === 'checkbox'">
              <b-form-checkbox
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  :value="true"
                  :unchecked-value="false"
              >
                {{ field.label }}
              </b-form-checkbox>
            </b-form-group>

            <!-- Number -->
            <b-form-group
                v-else-if="field.type === 'number'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  type="number"
                  :placeholder="field.placeholder || field.label"
                  :required="field.required"
                  :min="field.min"
                  :max="field.max"
              />
            </b-form-group>

            <!-- Date -->
            <b-form-group
                v-else-if="field.type === 'date'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <b-form-input
                  :id="`field-${field.key}`"
                  v-model="formData[field.key]"
                  type="date"
                  :required="field.required"
              />
            </b-form-group>

            <!-- Color -->
            <b-form-group
                v-else-if="field.type === 'color'"
                :label="field.label"
                :label-for="`field-${field.key}`"
            >
              <div class="d-flex align-items-center gap-2">
                <b-form-input
                    :id="`field-${field.key}`"
                    v-model="formData[field.key]"
                    type="color"
                    :required="field.required"
                    style="width: 60px; height: 40px;"
                />
                <b-form-input
                    v-model="formData[field.key]"
                    type="text"
                    :placeholder="field.placeholder || '#000000'"
                    :required="field.required"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    class="flex-grow-1"
                />
              </div>
              <small class="text-muted">Формат: #RRGGBB</small>
            </b-form-group>
          </div>
          <div class="d-flex flex-column gap-3 mt-4">
            <b-button type="submit" variant="primary" class="flex-grow-1">
              Сохранить
            </b-button>
            <b-button type="button" variant="secondary" class="mt-3" @click="handleClose">
              Отмена
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>
  </div>
</template>

<style scoped lang="scss">

#sidebar-right {
  overflow-y: hidden;
}

:deep(.b-sidebar-body) {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.sidebar-custom-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

fieldset {
  border: none !important;
}
</style>